import { ArrowLeft, Info, ShieldCheck, X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClipLoader } from 'react-spinners';
import toast from 'react-hot-toast';

import { AccentButton, NeutralButton } from '../../../../../../shared/Buttons';
import { Entity } from '../../../../../../types/apiTypes';
import useApi from '../../../../../../utils/AxiosWrapper';
import { BankAccountType, isValidRoutingNumber } from './types';

interface ManualLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  ownerId: string;
  entity: Entity;
  onSuccess?: () => void | Promise<void>;
}

type Step = 1 | 2;

interface StepValues {
  routing_number: string;
  account_number: string;
}

const EMPTY_STEP_VALUES: StepValues = {
  routing_number: '',
  account_number: '',
};

export const ManualLinkModal = ({
  isOpen,
  onClose,
  ownerId,
  entity,
  onSuccess,
}: ManualLinkModalProps) => {
  const { callProtectedApi } = useApi();

  const [step, setStep] = useState<Step>(1);
  const [bankAccountType, setBankAccountType] = useState<BankAccountType>(
    BankAccountType.Checking
  );
  const [initialValues, setInitialValues] =
    useState<StepValues>(EMPTY_STEP_VALUES);
  const [confirmValues, setConfirmValues] =
    useState<StepValues>(EMPTY_STEP_VALUES);
  const [fieldErrors, setFieldErrors] = useState<{
    routing_number?: string;
    account_number?: string;
  }>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Bank name lookup cache: routing_number -> bank name (null = looked up, no match).
  const [bankNameCache, setBankNameCache] = useState<
    Record<string, string | null>
  >({});
  const [lookupInflight, setLookupInflight] = useState(false);
  const inflightForRouting = useRef<string | null>(null);

  // Reset state whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setBankAccountType(BankAccountType.Checking);
      setInitialValues(EMPTY_STEP_VALUES);
      setConfirmValues(EMPTY_STEP_VALUES);
      setFieldErrors({});
      setServerError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const currentValues = step === 1 ? initialValues : confirmValues;
  const setCurrentValues = step === 1 ? setInitialValues : setConfirmValues;

  // Fire bank-name lookup whenever the routing number on the current step
  // becomes valid and we haven't already looked it up.
  useEffect(() => {
    const rn = currentValues.routing_number;
    if (!isValidRoutingNumber(rn)) return;
    if (rn in bankNameCache) return;
    if (inflightForRouting.current === rn) return;

    let cancelled = false;
    inflightForRouting.current = rn;
    setLookupInflight(true);

    (async () => {
      try {
        const response = await callProtectedApi(
          `/linkedAccounts/lookupBankName/${rn}`,
          'get',
          {},
          'Could not look up bank name.',
          {},
          undefined,
          0,
          true
        );
        if (cancelled) return;
        const name = response?.data?.data?.bank_name;
        setBankNameCache((prev) => ({
          ...prev,
          [rn]:
            typeof name === 'string' && name.trim().length > 0
              ? name.trim()
              : null,
        }));
      } catch {
        if (!cancelled) {
          setBankNameCache((prev) => ({ ...prev, [rn]: null }));
        }
      } finally {
        if (!cancelled) {
          setLookupInflight(false);
          inflightForRouting.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValues.routing_number]);

  const resolvedBankName = useMemo(() => {
    const rn = currentValues.routing_number;
    return rn in bankNameCache ? bankNameCache[rn] : undefined;
  }, [currentValues.routing_number, bankNameCache]);

  const sanitizeDigits = (raw: string, max: number) =>
    raw.replace(/\D/g, '').slice(0, max);

  const handleRoutingChange = (raw: string) => {
    setFieldErrors((prev) => ({ ...prev, routing_number: undefined }));
    setServerError(null);
    setCurrentValues({
      ...currentValues,
      routing_number: sanitizeDigits(raw, 9),
    });
  };

  const handleAccountChange = (raw: string) => {
    setFieldErrors((prev) => ({ ...prev, account_number: undefined }));
    setServerError(null);
    setCurrentValues({
      ...currentValues,
      account_number: sanitizeDigits(raw, 17),
    });
  };

  const validateStep1 = (): boolean => {
    const next: typeof fieldErrors = {};
    if (initialValues.routing_number.length !== 9) {
      next.routing_number = 'Routing number must be exactly 9 digits.';
    } else if (!isValidRoutingNumber(initialValues.routing_number)) {
      next.routing_number =
        'That routing number is not valid. Please double-check.';
    }
    if (
      initialValues.account_number.length < 4 ||
      initialValues.account_number.length > 17
    ) {
      next.account_number = 'Account number must be between 4 and 17 digits.';
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateStep2 = (): boolean => {
    const next: typeof fieldErrors = {};
    if (confirmValues.routing_number !== initialValues.routing_number) {
      next.routing_number = 'Routing numbers do not match. Please re-enter.';
    }
    if (confirmValues.account_number !== initialValues.account_number) {
      next.account_number = 'Account numbers do not match. Please re-enter.';
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (!validateStep1()) return;
    setConfirmValues(EMPTY_STEP_VALUES);
    setFieldErrors({});
    setServerError(null);
    setStep(2);
  };

  const handleBack = () => {
    setConfirmValues(EMPTY_STEP_VALUES);
    setFieldErrors({});
    setServerError(null);
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const response = await callProtectedApi(
        `/linkedAccounts/${ownerId}/manual`,
        'post',
        {
          account_number: initialValues.account_number,
          routing_number: initialValues.routing_number,
          bank_account_type: bankAccountType,
          entity,
        },
        'Failed to link bank account.',
        {},
        undefined,
        0,
        true
      );

      if (response?.status === HttpStatusCode.Created) {
        const bankLabel = resolvedBankName ? `${resolvedBankName} ` : '';
        toast.success(`Bank account linked. ${bankLabel}successfully added.`);
        await onSuccess?.();
        onClose();
        return;
      }

      const status = response?.status;
      const apiMsg: string | undefined = response?.data?.message;
      if (status === HttpStatusCode.Conflict) {
        setServerError('An account with these details is already linked.');
      } else if (status === HttpStatusCode.BadRequest) {
        setServerError(
          apiMsg || 'Those details are not valid. Please double-check.'
        );
      } else {
        setServerError(
          apiMsg ||
            'Something went wrong while linking your account. Please try again.'
        );
      }
    } catch (err: any) {
      setServerError(
        'Something went wrong while linking your account. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const labelClass = 'mb-1.5 block text-[13px] font-semibold text-primary';
  const helperClass = 'mt-1 text-[12px] leading-[16px] text-graysecondary';
  const errorClass = 'mt-1 text-[12px] leading-[16px] text-danger';

  const inputBase =
    'block w-full rounded-[4px] border-[1px] bg-white px-3 py-2.5 text-[14px] text-primary placeholder:text-graysecondary focus:outline-none focus:ring-1 disabled:bg-gray-50 disabled:cursor-not-allowed';
  const inputOk = 'border-bordergray focus:border-accent focus:ring-accent';
  const inputErr = 'border-danger focus:border-danger focus:ring-danger';

  const routingInputClass = `${inputBase} ${fieldErrors.routing_number ? inputErr : inputOk}`;
  const accountInputClass = `${inputBase} ${fieldErrors.account_number ? inputErr : inputOk}`;

  const stepLabel = step === 1 ? 'Enter details' : 'Confirm details';

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-50 px-4"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="w-full max-w-[520px] overflow-hidden rounded-[4px] bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-[1px] border-bordergray px-6 py-4">
          <div className="flex flex-col">
            <h2 className="text-[16px] font-semibold text-primary">
              Link a bank account manually
            </h2>
            <p className="mt-0.5 text-[12px] font-semibold uppercase tracking-wide text-graysecondary">
              Step {step} of 2 · {stepLabel}
            </p>
          </div>
          <X
            className={`text-graysecondary ${submitting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:text-primary'}`}
            size={22}
            weight="regular"
            onClick={submitting ? undefined : onClose}
          />
        </div>

        <div className="flex flex-col gap-5 px-6 py-6">
          {step === 2 && (
            <div className="flex items-start gap-2 rounded-[4px] border-[1px] border-bordergray bg-primarybg px-3 py-2.5">
              <Info
                size={18}
                weight="duotone"
                className="mt-0.5 shrink-0 text-primary"
              />
              <p className="text-[13px] leading-[18px] text-primary">
                To prevent typos, please re-enter your details. We've hidden
                what you typed so you can confirm cleanly.
              </p>
            </div>
          )}

          {serverError && (
            <div className="flex items-start gap-2 rounded-[4px] border-[1px] border-danger bg-bgred px-3 py-2.5">
              <Info
                size={18}
                weight="duotone"
                className="mt-0.5 shrink-0 text-danger"
              />
              <p className="text-[13px] leading-[18px] text-danger">
                {serverError}
              </p>
            </div>
          )}

          <div>
            <label className={labelClass} htmlFor="routing_number">
              {step === 1 ? 'Routing number' : 'Confirm routing number'}
            </label>
            <input
              id="routing_number"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={currentValues.routing_number}
              onChange={(e) => handleRoutingChange(e.target.value)}
              placeholder="9 digits"
              disabled={submitting}
              className={routingInputClass}
            />
            {fieldErrors.routing_number ? (
              <p className={errorClass}>{fieldErrors.routing_number}</p>
            ) : resolvedBankName ? (
              <p className="mt-1 flex items-center gap-1.5 text-[12px] font-semibold leading-[16px] text-secondary">
                <ShieldCheck size={14} weight="fill" />
                {resolvedBankName}
              </p>
            ) : lookupInflight ? (
              <p className="mt-1 text-[12px] leading-[16px] text-graysecondary">
                Looking up bank name…
              </p>
            ) : (
              <p className={helperClass}>
                9-digit number found on the bottom-left of a check.
              </p>
            )}
          </div>

          <div>
            <label className={labelClass} htmlFor="account_number">
              {step === 1 ? 'Account number' : 'Confirm account number'}
            </label>
            <input
              id="account_number"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={currentValues.account_number}
              onChange={(e) => handleAccountChange(e.target.value)}
              placeholder="4 to 17 digits"
              disabled={submitting}
              className={accountInputClass}
            />
            {fieldErrors.account_number ? (
              <p className={errorClass}>{fieldErrors.account_number}</p>
            ) : (
              <p className={helperClass}>
                Found on the bottom-right of a check, after the routing number.
              </p>
            )}
          </div>

          {step === 1 && (
            <div>
              <label className={labelClass}>Account type</label>
              <div
                role="radiogroup"
                aria-label="Account type"
                className="flex w-full rounded-[4px] border-[1px] border-bordergray bg-primarybg p-1"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={bankAccountType === BankAccountType.Checking}
                  onClick={() => setBankAccountType(BankAccountType.Checking)}
                  className={`flex-1 rounded-[4px] px-3 py-2 text-[14px] font-semibold transition ${
                    bankAccountType === BankAccountType.Checking
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-graysecondary hover:text-primary'
                  }`}
                >
                  Checking
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={bankAccountType === BankAccountType.Savings}
                  onClick={() => setBankAccountType(BankAccountType.Savings)}
                  className={`flex-1 rounded-[4px] px-3 py-2 text-[14px] font-semibold transition ${
                    bankAccountType === BankAccountType.Savings
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-graysecondary hover:text-primary'
                  }`}
                >
                  Savings
                </button>
              </div>
              <p className={helperClass}>
                Most personal accounts are checking.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="rounded-[4px] border-[1px] border-bordergray bg-primarybg px-3 py-2.5">
              <p className="text-[12px] font-semibold uppercase tracking-wide text-graysecondary">
                Account type
              </p>
              <p className="mt-0.5 text-[14px] font-semibold text-primary">
                {bankAccountType === BankAccountType.Checking
                  ? 'Checking'
                  : 'Savings'}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t-[1px] border-bordergray px-6 py-4">
          {step === 1 ? (
            <NeutralButton
              onClick={onClose}
              disabled={submitting}
              addClass="rounded-full h-[48px] flex-1"
            >
              Cancel
            </NeutralButton>
          ) : (
            <NeutralButton
              onClick={handleBack}
              disabled={submitting}
              addClass="rounded-full h-[48px] flex-1 gap-2"
            >
              <ArrowLeft size={16} weight="bold" />
              Back
            </NeutralButton>
          )}

          {step === 1 ? (
            <AccentButton
              onClick={handleNext}
              addClass="rounded-full h-[48px] flex-1 font-semibold"
            >
              Next
            </AccentButton>
          ) : (
            <AccentButton
              onClick={handleSubmit}
              disabled={submitting}
              addClass="rounded-full h-[48px] flex-1 font-semibold gap-2"
            >
              {submitting && (
                <ClipLoader
                  color="#1D213B"
                  loading={submitting}
                  size={14}
                  aria-label="Linking account"
                />
              )}
              {submitting ? 'Linking…' : 'Link account'}
            </AccentButton>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
